import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import cn from "classnames";
import { template } from "lodash";
import { Button, Checkbox, Message, toaster } from "rsuite";

import constants from "utils/constants";
import { isEveryWorkflowStepCompleted } from "utils/tenant-workflows/tenant-workflows-helper";
import {
    ITenantDeleteWorkflow,
    WorkflowModalType,
} from "utils/tenant-workflows/tenant-workflows-types";

import { getErrorMessage } from "api/defaults";
import {
    getTenantDelete,
    startTenantDelete,
} from "api/environments/tenants/tenant-delete-api";

import { ITenant } from "store/environments/tenants/tenants-types";

import ConfirmModal, {
    ConfirmationMode,
} from "components/Common/ConfirmModal/ConfirmModal";
import ProcessModalButton from "components/WorkflowLog/ProcessModalButton/ProcessModalButton";
import SignalRTenantDelete from "components/WorkflowLog/TenantDelete/SignalRTenantDelete";

import css from "components/BreadCrumb/BreadCrumb.module.css";
import "./DeleteTenantBar.less";

interface ComponentProps {
    envMoniker: string;
    activeEnvId: string;
    tenantMoniker: string;
    tenantId: string;
    activeTenant: ITenant;
    activeTenantDeleteWorkflow: ITenantDeleteWorkflow | null;
    isDeleteFetched: boolean;
    storeInvoiceVolumeEnabled: boolean;
    setLoaderVisibility: (visible: boolean) => void;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
    setTenantDeleteWorkflow: (workflow: ITenantDeleteWorkflow | null) => void;
}

const DeleteTenantBar: React.FC<ComponentProps> = ({
    envMoniker,
    activeEnvId,
    tenantMoniker,
    tenantId,
    activeTenant,
    activeTenantDeleteWorkflow,
    isDeleteFetched,
    setLoaderVisibility,
    setIsUnsavedChangeAvailable,
    setTenantDeleteWorkflow,
    storeInvoiceVolumeEnabled,
}: ComponentProps) => {
    const { t } = useTranslation();
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [deleteWorkflowId, setDeleteWorkflowId] = useState<string>("");
    const [confirmationDialogMessage, setConfirmationDialogMessage] =
        useState("");
    const [deleteCompleted, setDeleteCompleted] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [invoiceVolumeStoringValue, setInvoiceVolumeStoringValue] =
        useState<boolean>(storeInvoiceVolumeEnabled);
    const navigate = useNavigate();

    const handleCancel = () => {
        setConfirmVisible(false);
    };

    const handleConfirm = () => {
        setLoaderVisibility(true);
        setIsUnsavedChangeAvailable(false);
        startTenantDelete(envMoniker, tenantMoniker, {
            storeInvoiceDailyVolumes: invoiceVolumeStoringValue,
        }).then(([payload, status]) => {
            if (payload && status === constants.statusCode.OK) {
                getTenantDelete(envMoniker, tenantMoniker).then(
                    ([payload, status]) => {
                        setLoaderVisibility(false);
                        if (payload && status === constants.statusCode.OK) {
                            setTenantDeleteWorkflow(payload);
                        } else {
                            const errorMessage = getErrorMessage(
                                String(payload),
                                status
                            );
                            toaster.push(
                                <Message showIcon type="error">
                                    {errorMessage}
                                </Message>,
                                {
                                    duration: constants.errorAlertDurationMsec,
                                }
                            );
                        }
                    }
                );
                setDeleteWorkflowId(payload);
            } else {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error">
                        {errorMessage}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            }
        });
        setConfirmVisible(false);
    };

    const handleDelete = () => {
        setConfirmVisible(true);
    };

    function updateWorkflow(workflow: ITenantDeleteWorkflow) {
        setTenantDeleteWorkflow(workflow);
    }

    useEffect(() => {
        if (activeTenant) {
            setConfirmationDialogMessage(
                template(t("tenantDelete.areYouSure"))({
                    tenantName: activeTenant.versionInfo.name,
                })
            );
        }
    }, [activeTenant]);

    useEffect(() => {
        if (activeTenantDeleteWorkflow) {
            setDeleteCompleted(
                isEveryWorkflowStepCompleted(activeTenantDeleteWorkflow.steps)
            );
        }
    }, [activeTenantDeleteWorkflow]);

    useEffect(() => {
        if (deleteCompleted && activeTenantDeleteWorkflow) {
            navigate(
                `/environments/${activeEnvId}/deleted-tenant/${tenantId}`,
                { replace: true }
            );
        }
    }, [deleteCompleted]);

    useEffect(() => {
        if (
            activeTenantDeleteWorkflow &&
            activeTenantDeleteWorkflow.tenantId === activeTenant.versionInfo.id
        ) {
            setDeleteWorkflowId(activeTenantDeleteWorkflow.id);
        }
    }, [activeTenantDeleteWorkflow]);

    useEffect(() => {
        setIsDeleting(
            !!(
                activeTenant &&
                activeTenantDeleteWorkflow &&
                activeTenant.versionInfo.id ===
                    activeTenantDeleteWorkflow.tenantId
            )
        );
    }, [activeTenant, activeTenantDeleteWorkflow]);

    return (
        <>
            <ConfirmModal
                open={confirmVisible}
                message={confirmationDialogMessage}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                mode={ConfirmationMode.Destructive}
            >
                <Checkbox
                    className="confirmation-checkbox"
                    checked={invoiceVolumeStoringValue}
                    onChange={() =>
                        setInvoiceVolumeStoringValue(!invoiceVolumeStoringValue)
                    }
                >
                    {t("tenantDelete.storeInvoiceDailyVolumes")}
                </Checkbox>
            </ConfirmModal>

            <SignalRTenantDelete
                deleteWorkflowId={deleteWorkflowId}
                updateWorkflow={updateWorkflow}
            >
                {!deleteCompleted && (
                    <div style={{ display: "flex", flexDirection: "row" }}>
                        {isDeleting && activeTenantDeleteWorkflow && (
                            <ProcessModalButton
                                workflow={activeTenantDeleteWorkflow}
                                workflowType={WorkflowModalType.TenantDeletion}
                            />
                        )}
                        <Button
                            onClick={handleDelete}
                            disabled={isDeleting || !isDeleteFetched}
                            appearance="primary"
                            className={cn(css.addNewCustomerBtn, css.deleteBtn)}
                        >
                            {t("tenantDelete.deleteButton")}
                        </Button>
                    </div>
                )}
            </SignalRTenantDelete>
        </>
    );
};

export default DeleteTenantBar;
